import { Link, Navigate } from "react-router-dom";
import { useState } from "react";

import { AbcFitnessBrandLogo } from "@/components/portal/AbcFitnessBrandLogo";
import { GlofoxBrandLogo } from "@/components/portal/GlofoxBrandLogo";
import { useColorTheme } from "@/hooks/useColorTheme";
import { usePageTitle } from "@/hooks/usePageTitle";
import { requestAdminPasswordReset } from "@/lib/api/admin-forgot-password";
import {
  getForgotPasswordErrorMessage,
  getForgotPasswordSuccessMessage,
  isForgotPasswordFormValid,
  toForgotPasswordRequest,
} from "@/lib/api/forgot-password-form";
import { getAdminAuthSession } from "@/lib/auth/admin-session";

import "@/styles/admin-panel.css";

export default function AdminForgotPasswordPage() {
  usePageTitle("Admin forgot password");
  useColorTheme();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (getAdminAuthSession()) {
    return <Navigate to="/admin/control-panel" replace />;
  }

  const canSubmit = isForgotPasswordFormValid({ email });

  async function handleSubmit() {
    if (!canSubmit || loading || Boolean(success)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await requestAdminPasswordReset(
        toForgotPasswordRequest({ email }),
      );
      setSuccess(getForgotPasswordSuccessMessage(response));
    } catch (err: unknown) {
      setError(getForgotPasswordErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="admin-login" data-testid="admin-forgot-password-shell">
      <div className="admin-login-panel">
        <div className="admin-login-brand">
          <div className="admin-login-brand-row">
            <AbcFitnessBrandLogo
              to=""
              testId="admin-forgot-password-abc-logo"
              theme="dark"
            />
            <span className="admin-login-sep" aria-hidden>
              /
            </span>
            <GlofoxBrandLogo to="" testId="admin-forgot-password-glofox-logo" />
          </div>
          <div className="admin-login-product">API Admin Panel</div>
        </div>

        <div
          className="auth-card admin-login-card"
          data-testid="admin-forgot-password-card"
        >
          <div className="auth-hdr admin-login-hdr">
            <p className="auth-eye">Password reset</p>
            <h2 data-testid="admin-forgot-password-title">Forgot your password?</h2>
            <p>
              Enter your admin email and we&apos;ll send you a link to choose a
              new password.
            </p>
          </div>
          <div className="auth-body">
            {error ? (
              <div
                className="error-msg"
                data-testid="admin-forgot-password-error"
                role="alert"
              >
                <i className="ti ti-alert-circle" aria-hidden="true" />
                <span>{error}</span>
              </div>
            ) : null}

            {success ? (
              <div
                className="success-msg"
                data-testid="admin-forgot-password-success"
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-start",
                  padding: "12px 14px",
                  borderRadius: 8,
                  background: "rgba(0, 181, 136, 0.08)",
                  color: "var(--t1)",
                  fontSize: 13,
                  marginBottom: "1rem",
                }}
              >
                <i className="ti ti-circle-check" style={{ color: "var(--abc)" }} />
                <span>{success}</span>
              </div>
            ) : null}

            <div className="field">
              <label htmlFor="admin-forgot-password-email">Email address</label>
              <input
                id="admin-forgot-password-email"
                type="email"
                data-testid="admin-forgot-password-email"
                placeholder="you@company.com"
                autoComplete="username"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                  setSuccess(null);
                }}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                disabled={loading}
              />
            </div>

            <button
              type="button"
              className="submit-btn admin-login-submit"
              data-testid="admin-forgot-password-submit"
              onClick={handleSubmit}
              disabled={loading || !canSubmit || Boolean(success)}
            >
              {loading ? (
                <>
                  <i
                    className="ti ti-loader-2 admin-login-spinner"
                    aria-hidden="true"
                  />{" "}
                  Sending…
                </>
              ) : (
                <>
                  <i className="ti ti-mail" aria-hidden="true" /> Send reset link
                </>
              )}
            </button>
          </div>
        </div>

        <p className="already" style={{ textAlign: "center", marginTop: "1rem" }}>
          Remember your password?{" "}
          <Link to="/admin/login" data-testid="admin-forgot-password-login-link">
            Admin sign in →
          </Link>
        </p>
      </div>
    </div>
  );
}
